import React from 'react';
import {useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {DateRange} from 'rsuite/DateRangePicker';
import {t} from '@lingui/core/macro';

import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import Select from 'shared/ui/Select/Select';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import ListFiltersForm from 'shared/ui/Modal/ListFiltersForm/ListFiltersForm';
import {IFiltersFormComponentProps} from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import {
    dateRangeToFilters,
    getUrlDateRange,
    TAGS_FILTER_PARAM,
    tagsToUrlFilters,
    UrlFiltersData,
} from 'shared/lib/filterHelper';
import {TagsFilterField} from 'entities/monitor';
import {ServerStatus} from 'features/server/model/Server/IServer';
import {getServerStatusLabel} from 'features/server/lib/serverStatusHelper';

const SERVER_STATUSES: ServerStatus[] = ['pending', 'online', 'offline', 'paused', 'suspended'];

interface IServersFiltersForm {
    tags?: string[],
    status?: string,
    os?: string,
    agentVersion?: string,
    lastSeenAt?: DateRange,
    createdAt?: DateRange,
}

const ServersFiltersForm: React.FC<IFiltersFormComponentProps> = ({onClose}) => {
    const [searchParams] = useSearchParams();
    const activeTags = searchParams.getAll(TAGS_FILTER_PARAM);

    const {register, handleSubmit, control} = useForm<IServersFiltersForm>({
        defaultValues: {
            tags: activeTags,
            status: searchParams.get('status') ?? undefined,
            os: searchParams.get('os') ?? undefined,
            agentVersion: searchParams.get('agent_version') ?? undefined,
            lastSeenAt: getUrlDateRange(searchParams, 'last_seen_at'),
            createdAt: getUrlDateRange(searchParams, 'created_at'),
        },
    });

    const statusOptions = SERVER_STATUSES.map(status => ({label: getServerStatusLabel(status), value: status}));

    const toFilters = ({tags, status, os, agentVersion, lastSeenAt, createdAt}: IServersFiltersForm) => {
        const data: UrlFiltersData = {
            ...tagsToUrlFilters(tags),
            status: status ?? undefined,
            os: os?.trim(),
            agent_version: agentVersion?.trim(),
        };
        dateRangeToFilters(lastSeenAt, data, 'last_seen_at');
        dateRangeToFilters(createdAt, data, 'created_at');

        return data;
    };

    return (
        <ListFiltersForm handleSubmit={handleSubmit} toFilters={toFilters} onClose={onClose}>
            <TagsFilterField control={control}/>

            <FormGroupWithError label={t`Состояние`}>
                <Select control={control} name="status" data={statusOptions} cleanable={true} searchable={false}/>
            </FormGroupWithError>

            <FormGroupWithError label={t`ОС или её часть`}>
                <InputText register={register} control={control} name="os" placeholder=""/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Версия агента или её часть`}>
                <InputText register={register} control={control} name="agentVersion" placeholder=""/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Период последней активности`}>
                <DateRangePickerControlled control={control} name="lastSeenAt" cleanable={true}/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Период добавления`}>
                <DateRangePickerControlled control={control} name="createdAt" cleanable={true}/>
            </FormGroupWithError>
        </ListFiltersForm>
    );
};

export default ServersFiltersForm;
